import * as _ from "lodash";
import React, { useState, useEffect, useRef } from "react";
import Cookies from "js-cookie";
import {
    ApolloClient,
    InMemoryCache,
    ApolloProvider,
    from,
    gql,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { setContext } from "@apollo/client/link/context";
import createUploadLink from "apollo-upload-client/createUploadLink.mjs";
import { get, post } from "../services/HttpService";
import { proxies } from "../services/RepositoryService";
import { isBitSet } from "../utils";

const headers = {
    "Content-Type": "application/json",
};

export const PERMISSIONS = {
    READ: 1,
    WRITE: 2,
    DESTROY: 4,
    EXECUTE: 8,
};

const toNumericBitLocation = (value) => {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string" && value.trim() !== "") {
        const parsed = Number(value);
        if (Number.isFinite(parsed)) return parsed;
    }
    return null;
};

const resolvePermissionBitLocations = (types) => {
    const flags = types?.permissions?.flags || {};
    return {
        read: toNumericBitLocation(flags.BIT_LOCATION_READ) ?? 0,
        write: toNumericBitLocation(flags.BIT_LOCATION_WRITE) ?? 1,
        destroy:
            toNumericBitLocation(flags.BIT_LOCATION_DESTROY) ??
            toNumericBitLocation(flags.BIT_LOCATION_DELETE) ??
            2,
        execute: toNumericBitLocation(flags.BIT_LOCATION_EXECUTE) ?? 3,
    };
};

const StarekContext = React.createContext({
    snackbarNotification: null,
    auth: null,

    lastUsedWorkspace: null,
    lastUsedPlant: null,
    subscription: null,

    engineClient: null,
    memberships: [],
    permissionMap: {},
    can: (entityCode, action) => false,
    withinLimit: (resource) => true,
    hasReachedLimit: (resource) => false,

    setSnackbarNotification: () => {
        console.log("provide implementation");
    },
    showMessage: () => {
        console.log("provide implementation");
    },
    showError: () => {
        console.log("provide implementation");
    },
    showSuccess: () => {
        console.log("provide implementation");
    },
    showWarning: () => {
        console.log("provide implementation");
    },
    showInfo: () => {
        console.log("provide implementation");
    },
    logout: () => {
        console.log("provide implementation");
    },
    login: (workspace, username, password) => {
        console.log("provide implementation");
    },
    ssoLogin: (parameters) => {
        console.log("provide implementation");
    },
});

const createEngineClient = (workspace, authToken, onUnauthorized) => {
    if (!workspace) return null;
    const uri = `${workspace.engine.scheme || "http"}://${workspace.engine.host
        }:${workspace.engine.port}/api/v1/${workspace.workspace_id}/graphql`;

    const uploadLink = createUploadLink({ uri });
    const authLink = setContext((_, { headers }) => {
        return {
            headers: {
                ...headers,
                Authorization: authToken ? `Bearer ${authToken}` : "",
            },
        };
    });

    const errorLink = onError(({ networkError }) => {
        if (networkError?.statusCode === 401) {
            onUnauthorized?.();
        }
    });

    return new ApolloClient({
        link: from([errorLink, authLink, uploadLink]),
        cache: new InMemoryCache(),
    });
};

export const StarekContextProvider = (props) => {
    const [snackbarNotification, setSnackbarNotification] = useState(null);
    const [authToken, setAuthToken] = useState(Cookies.get("token"));
    const [user, setUser] = useState(JSON.parse(localStorage.getItem("user")));
    const [lastUsedWorkspace, setLastUsedWorkspace] = useState(
        JSON.parse(localStorage.getItem("lastUsedWorkspace")),
    );
    const [lastUsedPlant, setLastUsedPlant] = useState(
        JSON.parse(localStorage.getItem("lastUsedPlant")),
    );
    const [availablePlants, setAvailablePlants] = useState([]);
    // Raw memberships array — used for plant list filtering in ChoosePlant
    const [memberships, setMemberships] = useState(
        JSON.parse(localStorage.getItem("memberships") || "[]"),
    );
    // Pre-built map: { [plant_id]: { [entity_code]: { READ, WRITE, DESTROY, EXECUTE } } }
    const [permissionMap, setPermissionMap] = useState(
        JSON.parse(localStorage.getItem("permissionMap") || "{}"),
    );
    // Subscription info: plan, limits, usage
    const [subscription, setSubscription] = useState(
        JSON.parse(localStorage.getItem("subscription") || "null"),
    );

    const [unauthorizedFlag, setUnauthorizedFlag] = useState(false);

    const handleUnauthorized = () => setUnauthorizedFlag(true);

    const [engineClient, setEngineClient] = useState(
        createEngineClient(lastUsedWorkspace, authToken, handleUnauthorized),
    );
    const [types, setTypes] = useState(null);

    const engineClientRef = useRef(engineClient);
    useEffect(() => {
        engineClientRef.current = engineClient;
    }, [engineClient]);

    useEffect(() => {
        if (!user) return;
        localStorage.setItem("user", JSON.stringify(user));
    }, [user]);

    useEffect(() => {
        if (!authToken) {
            Cookies.remove("token");
            localStorage.removeItem("starek_report_filters");
            localStorage.removeItem("starek_report_range");
            return;
        }
        Cookies.set("token", authToken);
        const interval = setInterval(() => {
            const cookieAuth = Cookies.get("token");
            if (cookieAuth !== authToken) {
                setAuthToken(cookieAuth);
            }
        }, 60000);
        return () => clearInterval(interval);
    }, [authToken]);

    useEffect(() => {
        if (!lastUsedPlant) return;
        localStorage.setItem("lastUsedPlant", JSON.stringify(lastUsedPlant));
    }, [lastUsedPlant]);

    useEffect(() => {
        if (!lastUsedWorkspace) return;

        const fetchData = async () => {
            localStorage.setItem(
                "lastUsedWorkspace",
                JSON.stringify(lastUsedWorkspace),
            );
            setEngineClient(
                createEngineClient(
                    lastUsedWorkspace,
                    authToken,
                    handleUnauthorized,
                ),
            );
            const response = await fetchTypes(lastUsedWorkspace);
            if (response) {
                setTypes(response);
            }
        };

        fetchData();
    }, [lastUsedWorkspace, authToken, lastUsedPlant]);

    const showMessage = async (message, type = "info") => {
        // Handle Error objects
        let displayMessage = message;
        if (message instanceof Error) {
            displayMessage = message.message || "An error occurred";
        } else if (typeof message === "object" && message !== null) {
            // Handle other objects (like GraphQL errors)
            displayMessage = message.message || JSON.stringify(message);
        }

        await setSnackbarNotification({
            type: type,
            text: displayMessage || "No message provided",
        });
    };

    const showError = async (error) => {
        let errorMessage = "There was an error processing this request";

        if (error instanceof Error) {
            errorMessage = error.message;
        } else if (typeof error === "string") {
            errorMessage = error;
        } else if (typeof error === "object" && error !== null) {
            errorMessage = error.message || JSON.stringify(error);
        }

        await showMessage(errorMessage, "error");
    };

    const showSuccess = async (message) => {
        await showMessage(
            message || "Operation completed successfully",
            "success",
        );
    };

    const showWarning = async (warning) => {
        await showMessage(
            warning || "There was a warning processing this request",
            "warning",
        );
    };

    const showInfo = async (message) => {
        await showMessage(message || "Here is some information", "info");
    };

    useEffect(() => {
        if (user?.id && authToken && memberships.length === 0) {
            loadMemberships(user.id);
        }
    }, [user?.id, authToken]);

    useEffect(() => {
        localStorage.setItem("memberships", JSON.stringify(memberships));
    }, [memberships]);

    useEffect(() => {
        localStorage.setItem("permissionMap", JSON.stringify(permissionMap));
    }, [permissionMap]);

    useEffect(() => {
        localStorage.setItem("subscription", JSON.stringify(subscription));
    }, [subscription]);

    const withinLimit = (resource) => {
        if (!subscription) return true;
        const limit = subscription.limits?.[resource];
        const usage = subscription.usage?.[resource];
        if (limit === undefined || limit === -1) return true;
        if (usage === undefined) return true;
        return usage < limit;
    };

    const hasReachedLimit = (resource) => !withinLimit(resource);

    const loadSubscription = async (workspace) => {
        if (!workspace) return;
        try {
            const url = `${workspace.engine.scheme}://${workspace.engine.host}:${workspace.engine.port}${process.env.REACT_APP_API_PREFIX}/${workspace.workspace_id}/subscription`;
            const token = Cookies.get("token");
            const response = await fetch(url, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: token ? `Bearer ${token}` : "",
                },
            });
            if (response.ok) {
                const data = await response.json();
                setSubscription(data);
            }
        } catch (err) {
            console.warn("Failed to load subscription info", err);
        }
    };

    const buildPermissionMap = (membershipList) => {
        const map = {};
        const permissionBits = resolvePermissionBitLocations(types);
        for (const { plant_id, role } of membershipList) {
            if (!role?.privileges) continue;
            map[plant_id] = {};
            for (const { permission, entity_type } of role.privileges) {
                const code = entity_type?.code?.toLowerCase();
                if (!code) continue;
                map[plant_id][code] = {
                    READ: isBitSet(permission, permissionBits.read),
                    WRITE: isBitSet(permission, permissionBits.write),
                    DESTROY: isBitSet(permission, permissionBits.destroy),
                    EXECUTE: isBitSet(permission, permissionBits.execute),
                };
            }
        }
        return map;
    };

    const can = (entityCode, action) => {
        if (!lastUsedPlant?.id) return false;
        return (
            permissionMap?.[lastUsedPlant.id]?.[entityCode]?.[
            action.toUpperCase()
            ] ?? false
        );
    };

    const loadMemberships = async (userId) => {
        const client = engineClientRef.current;
        if (!client) return [];
        try {
            const MEMBERSHIPS_QUERY = gql`
                query GetUserMemberships($where: MembershipWhereInput!) {
                    memberships(where: $where) {
                        user_id
                        plant_id
                        plant {
                            id
                            name
                            code
                        }
                        role {
                            id
                            name
                            privileges {
                                permission
                                entity_type {
                                    code
                                    singular
                                }
                            }
                        }
                    }
                }
            `;
            const { data } = await client.query({
                query: MEMBERSHIPS_QUERY,
                variables: { where: { user_id: { equals: parseInt(userId) } } },
                fetchPolicy: "network-only",
            });
            const list = data?.memberships ?? [];
            setMemberships(list);
            setPermissionMap(buildPermissionMap(list));
            return list;
        } catch (err) {
            console.warn("Failed to load memberships", err);
            return [];
        }
    };

    const login = async (workspace, username, password) => {
        const foundWorkspace = await proxies(workspace);
        setLastUsedWorkspace(foundWorkspace);

        const client = createEngineClient(
            foundWorkspace,
            authToken,
            handleUnauthorized,
        );
        setEngineClient(client);
        engineClientRef.current = client;

        const url = `${foundWorkspace.engine.scheme}://${foundWorkspace.engine.host}:${foundWorkspace.engine.port}${process.env.REACT_APP_API_PREFIX}/authenticate`;
        const body = {
            workspace: foundWorkspace.workspace_id,
            username: username,
            password: password,
        };
        const auth = await post(url, body, headers);

        setUser(auth.user);
        setAuthToken(auth.token);

        const authedClient = createEngineClient(
            foundWorkspace,
            auth.token,
            handleUnauthorized,
        );
        setEngineClient(authedClient);
        engineClientRef.current = authedClient;

        await loadMemberships(auth.user.id);
        await loadSubscription(foundWorkspace);
        return auth;
    };

    const ssoLogin = async (parameters) => {
        console.log("SSO login called with", parameters);
        const { code, workspace, returnUriHost } = parameters;
        const foundWorkspace = await proxies(workspace);
        setLastUsedWorkspace(foundWorkspace);

        const client = createEngineClient(
            foundWorkspace,
            authToken,
            handleUnauthorized,
        );
        setEngineClient(client);
        engineClientRef.current = client;

        const url = `${foundWorkspace.engine.scheme}://${foundWorkspace.engine.host}:${foundWorkspace.engine.port}${process.env.REACT_APP_API_PREFIX}/sso-authenticate`;
        const body = {
            code,
            workspace: foundWorkspace.workspace_id,
            returnUriHost,
        };
        const auth = await post(url, body, headers);
        setUser(auth.user);
        setAuthToken(auth.token);

        const authedClient = createEngineClient(
            foundWorkspace,
            auth.token,
            handleUnauthorized,
        );
        setEngineClient(authedClient);
        engineClientRef.current = authedClient;

        await loadMemberships(auth.user.id);
        await loadSubscription(foundWorkspace);
        return auth;
    };

    const fetchTypes = async (workspace) => {
        const url = `${workspace.engine.scheme}://${workspace.engine.host}:${workspace.engine.port}/api/v1/_types`;
        try {
            const response = await post(url, {
                headers: {
                    "Content-Type": "application/json",
                },
            });
            return response;
        } catch (error) {
            await showError(error.message || "Failed to fetch types");
            throw error;
        }
    };

    const logout = async () => {
        setAuthToken(null);
        setMemberships([]);
        setPermissionMap({});
        setSubscription(null);
        localStorage.removeItem("memberships");
        localStorage.removeItem("permissionMap");
        localStorage.removeItem("subscription");
    };

    return (
        <StarekContext.Provider
            value={{
                snackbarNotification,
                auth: authToken,
                lastUsedWorkspace,
                lastUsedPlant,
                availablePlants,
                user,
                memberships,
                permissionMap,
                subscription,
                can,
                withinLimit,
                hasReachedLimit,
                loadMemberships,
                loadSubscription,
                setSnackbarNotification,
                showMessage,
                showError,
                showSuccess,
                showWarning,
                showInfo,
                setAuth: setAuthToken,
                setLastUsedWorkspace,
                setLastUsedPlant,
                setAvailablePlants,
                logout,
                login,
                ssoLogin,
                types,
                engineClient,
                unauthorizedFlag,
                setUnauthorizedFlag,
            }}
        >
            {engineClient ? (
                <ApolloProvider client={engineClient}>
                    {props.children}
                </ApolloProvider>
            ) : (
                <>{props.children}</>
            )}
        </StarekContext.Provider>
    );
};

export default StarekContext;
